import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Switch,
  FormControlLabel,
  Chip,
  Stack,
  Alert,
  Button,
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";

export interface FeatureInsightsConfig {
  task_type: "default" | "battlecard" | "hiring" | "opinions";
  task_role_name: string;
  task_role: string;
  task_goal: string;
  task_style: string;
  min_words_between_insights: number;
  max_insights_per_session: number;
  battlecard_namespace_assets?: {
    namespace_assets: Record<string, string[]>;
  } | null;
  // Deprecated fields
  namespace?: string;
  collection_name?: string | string[];
}

interface FeatureInsightsConfigEditorProps {
  value: FeatureInsightsConfig | null;
  onChange: (config: FeatureInsightsConfig | null) => void;
  organizationId?: string;
  disabled?: boolean;
}

const FeatureInsightsConfigEditor: React.FC<
  FeatureInsightsConfigEditorProps
> = ({ value, onChange, organizationId, disabled = false }) => {
  const [enabled, setEnabled] = useState(!!value);
  const [config, setConfig] = useState<FeatureInsightsConfig>({
    task_type: "default",
    task_role_name: "",
    task_role: "",
    task_goal: "",
    task_style: "",
    min_words_between_insights: 0,
    max_insights_per_session: -1,
    battlecard_namespace_assets: null,
    namespace: "",
    collection_name: "",
  });

  // Collection state
  const [collections, setCollections] = useState<string[]>([]);
  const [newCollection, setNewCollection] = useState("");

  useEffect(() => {
    if (value) {
      setConfig(value);
      setEnabled(true);

      // Initialize collections from value
      if (value.collection_name) {
        if (typeof value.collection_name === "string") {
          setCollections([value.collection_name]);
        } else if (Array.isArray(value.collection_name)) {
          setCollections(value.collection_name);
        }
      }
    } else {
      setEnabled(false);
    }
  }, [value]);

  const handleToggle = (checked: boolean) => {
    setEnabled(checked);
    if (!checked) {
      onChange(null);
    } else {
      onChange(config);
    }
  };

  const handleConfigChange = (updates: Partial<FeatureInsightsConfig>) => {
    const newConfig = { ...config, ...updates };
    setConfig(newConfig);
    if (enabled) {
      onChange(newConfig);
    }
  };

  const handleAddCollection = () => {
    if (newCollection.trim()) {
      const updated = [...collections, newCollection.trim()];
      setCollections(updated);
      handleConfigChange({ collection_name: updated });
      setNewCollection("");
    }
  };

  const handleRemoveCollection = (index: number) => {
    const updated = collections.filter((_, i) => i !== index);
    setCollections(updated);
    handleConfigChange({ collection_name: updated });
  };

  const getTaskTypeHelp = (type: string) => {
    switch (type) {
      case "battlecard":
        return "Sales battlecard mode - helps address customer concerns and objections";
      case "hiring":
        return "Hiring mode - assists with recruitment and candidate evaluation";
      case "opinions":
        return "Opinions mode - generates engaging opinions and responses";
      default:
        return "Default mode - general purpose insights";
    }
  };

  return (
    <Box>
      <FormControlLabel
        control={
          <Switch
            checked={enabled}
            onChange={(e) => handleToggle(e.target.checked)}
            disabled={disabled}
          />
        }
        label={
          <Typography variant="subtitle1" fontWeight="medium">
            Enable Feature Insights
          </Typography>
        }
      />

      {enabled && (
        <Box sx={{ mt: 2, pl: 2 }}>
          <Stack spacing={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Task Type</InputLabel>
              <Select
                value={config.task_type}
                onChange={(e) =>
                  handleConfigChange({
                    task_type: e.target
                      .value as FeatureInsightsConfig["task_type"],
                  })
                }
                label="Task Type"
                disabled={disabled}
              >
                <MenuItem value="default">Default</MenuItem>
                <MenuItem value="battlecard">Battlecard</MenuItem>
                <MenuItem value="hiring">Hiring</MenuItem>
                <MenuItem value="opinions">Opinions</MenuItem>
              </Select>
            </FormControl>

            <Alert severity="info" sx={{ mt: 1 }}>
              {getTaskTypeHelp(config.task_type)}
            </Alert>

            <TextField
              fullWidth
              label="Task Role Name (Optional)"
              value={config.task_role_name}
              onChange={(e) =>
                handleConfigChange({ task_role_name: e.target.value })
              }
              size="small"
              disabled={disabled}
              helperText="Name for the AI role (e.g., 'Anatoly', 'Jane'). If blank, uses the bot name."
            />

            <TextField
              fullWidth
              label="Task Role"
              value={config.task_role}
              onChange={(e) =>
                handleConfigChange({ task_role: e.target.value })
              }
              multiline
              rows={2}
              size="small"
              disabled={disabled}
              helperText="Who is the AI with regard to insights? What role are they playing?"
              required
            />

            <TextField
              fullWidth
              label="Task Goal"
              value={config.task_goal}
              onChange={(e) =>
                handleConfigChange({ task_goal: e.target.value })
              }
              multiline
              rows={3}
              size="small"
              disabled={disabled}
              helperText="What should the AI try to accomplish with these insights?"
              required
            />

            <TextField
              fullWidth
              label="Task Style (Optional)"
              value={config.task_style}
              onChange={(e) =>
                handleConfigChange({ task_style: e.target.value })
              }
              multiline
              rows={2}
              size="small"
              disabled={disabled}
              helperText="How should responses be written? (tone, style, personality)"
            />

            <TextField
              fullWidth
              label="Min Words Between Insights"
              type="number"
              value={config.min_words_between_insights}
              onChange={(e) =>
                handleConfigChange({
                  min_words_between_insights: parseFloat(e.target.value) || 0,
                })
              }
              size="small"
              disabled={disabled}
              helperText="Cooldown period in words before triggering another insight (0 = no cooldown)"
              inputProps={{ min: 0, step: 50 }}
            />

            <TextField
              fullWidth
              label="Max Insights Per Session"
              type="number"
              value={config.max_insights_per_session}
              onChange={(e) =>
                handleConfigChange({
                  max_insights_per_session: parseInt(e.target.value) || -1,
                })
              }
              size="small"
              disabled={disabled}
              helperText="Maximum number of insights to offer in a session (-1 = no limit)"
              inputProps={{ min: -1, step: 1 }}
            />

            {/* Feature Insights Assets - Hidden for now */}
            {/* <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Feature Insights Assets
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {config.task_type === 'battlecard' 
                  ? 'Select battlecard documents to help address customer concerns'
                  : 'Select relevant assets for generating insights'}
              </Typography>
              <AssetPicker
                namespace={
                  organizationId ? `${organizationId}:assessments` : ""
                }
                selectedNamespaceAssetRegistry={
                  config.battlecard_namespace_assets || null
                }
                onSelectionChange={(registry) =>
                  handleConfigChange({ battlecard_namespace_assets: registry })
                }
              />
            </Box> */}

            {/* Namespace and Collection fields */}
            <TextField
              fullWidth
              label="Namespace"
              value={config.namespace || ""}
              onChange={(e) =>
                handleConfigChange({ namespace: e.target.value })
              }
              size="small"
              disabled={disabled}
              helperText="The asset namespace to use"
            />

            <Box>
              <Typography variant="caption" sx={{ mb: 1 }}>
                Asset Ids within the namespace to use for insights
              </Typography>
              <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
                <TextField
                  size="small"
                  value={newCollection}
                  onChange={(e) => setNewCollection(e.target.value)}
                  placeholder="Add collection name"
                  disabled={disabled}
                  sx={{ flex: 1 }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleAddCollection}
                  disabled={disabled || !newCollection.trim()}
                  startIcon={<AddIcon />}
                >
                  Add
                </Button>
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {collections.map((collection, index) => (
                  <Chip
                    key={index}
                    label={collection}
                    onDelete={
                      disabled ? undefined : () => handleRemoveCollection(index)
                    }
                    size="small"
                  />
                ))}
              </Stack>
            </Box>
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default FeatureInsightsConfigEditor;
